import { Component, ElementRef, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SobModalListComponent } from '../sob-modal-list/sob-modal-list.component';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, take, takeUntil } from 'rxjs';
import { SobResponse } from '../../../../models/http/sob-http.model';
import { TpaHttpService } from '../../../../services/tpa-http.service';
import { TpaService } from '../../../../services/tpa.service';
import { SobModalEmitter } from '../../../../models/sob.model';
import { CustomModalComponent } from '../../../../../../shared/components/custom-modal/custom-modal.component';
import { ModalPdfComponent } from '../sob-modal-pdf/modal-pdf.component';
import { PdfGenerationService } from '../../../../../../shared/services/pdf-generation.service';
import { ModalAction } from '../../../../../../shared/components/custom-modal/models/custom-modal.model';

@Component({
  selector: 'app-tpa-dialog',
  standalone: true,
  imports: [
    CommonModule,
    SobModalListComponent,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ModalPdfComponent,
  ],
  templateUrl: './tpa-dialog.component.html',
  styleUrl: './tpa-dialog.component.scss',
})
export class TpaDialogComponent implements OnInit, OnDestroy {
  @ViewChild('dialogContent') dialogContent!: TemplateRef<any>;
  @ViewChild('dialogContentPdf') dialogContentPdf!: TemplateRef<any>;
  @ViewChild('sobContentWrapper', { read: ElementRef }) sobContentWrapper?: ElementRef;

  sobData!: SobResponse;
  pdf!: Blob;

  private unsubscribe$ = new Subject<void>();

  private dialog = inject(MatDialog);
  private tpaService = inject(TpaService);
  private tpaHttpService = inject(TpaHttpService);
  private pdfGenerationService = inject(PdfGenerationService);
  private snackBar = inject(MatSnackBar);

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initSubscriptions() {
    this.tpaService.openSobDialog$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((sobModalData: SobModalEmitter) => {
        if (sobModalData.showPdf) {
          this.showSobPdf(sobModalData.planId);
          return;
        }

        this.showSobPlan(sobModalData.planId);
      });

    this.tpaService.openPolicyDialog$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((policyId: string) => {
        if (!policyId) {
          return;
        }

        this.showPolicyPdf(policyId);
      });
  }

  private showSobPlan(planId: string) {
    this.tpaHttpService
      .getScheduleOfBenefitsPlan(planId)
      .pipe(take(1))
      .subscribe((res) => {
        this.sobData = res;

        const modalActions: ModalAction[] = [
          {
            label: 'Download as PDF',
            callback: () => this.downloadSobAsPdf(),
            color: 'primary',
            icon: 'download',
          },
        ];

        this.dialog.open(CustomModalComponent, {
          data: {
            title: 'Schedule of Benefits',
            width: '80%',
            template: this.dialogContent,
            actions: modalActions,
          },
        });
      });
  }

  private async downloadSobAsPdf(): Promise<void> {
    // Show loading notification
    const snackBarRef = this.snackBar.open('Generating PDF, please wait...', '', {
      duration: undefined, // Keep open until dismissed
    });

    try {
      // Wait a bit to ensure the modal content is fully rendered
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Find the SOB content element in the DOM
      const sobElement = document.querySelector('.sob') as HTMLElement;

      if (!sobElement) {
        snackBarRef.dismiss();
        this.snackBar.open('Error: Could not find content to export', 'Close', {
          duration: 5000,
        });
        return;
      }

      const planName = this.sobData?.name || 'Schedule_of_Benefits';
      await this.pdfGenerationService.generatePdfFromHtml(sobElement, planName);

      // Dismiss loading and show success
      snackBarRef.dismiss();
      this.snackBar.open('PDF downloaded successfully!', 'Close', {
        duration: 3000,
      });
    } catch (error) {
      snackBarRef.dismiss();
      console.error('Error generating PDF:', error);
      this.snackBar.open('Error generating PDF. Please try again.', 'Close', {
        duration: 5000,
      });
    }
  }

  private showSobPdf(planId: string) {
    this.tpaHttpService
      .getScheduleOfBenefitsPlanDoc(planId)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        const isPdf = res.type === 'application/pdf';

        this.pdf = res;
        if (isPdf) {
          this.dialog.open(CustomModalComponent, {
            data: {
              title: 'Schedule of Benefits PDF',
              width: '80%',
              template: this.dialogContentPdf,
              noScroll: true,
            },
          });
        } else {
          this.downloadDoc(res, `Schedule_of_Benefits.pdf`);
        }
      });
  }

  private showPolicyPdf(policyId: string) {
    this.tpaHttpService
      .getPolicyDoc(policyId)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        const isPdf = res.type === 'application/pdf';

        this.pdf = res;
        if (isPdf) {
          this.dialog.open(CustomModalComponent, {
            data: {
              title: 'Policy',
              width: '80%',
              template: this.dialogContentPdf,
              noScroll: true,
            },
          });
        } else {
          return;
        }
      });
  }

  private downloadDoc(blobRes: Blob, fileName: string) {
    const blob = new Blob([blobRes], { type: blobRes.type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
